import { EU_COUNTRIES } from './euCountries.js';
import type { SessionAgency } from '../session/types.js';

/**
 * Why the buy page will not let this account buy - D-16 item 6.
 *
 * **The same three rules the server enforces, and the server is the one that
 * counts.** `POST /api/checkout/sessions` asks its own copy on every request,
 * so a stale bundle, an old tab or a script still meets a 403. This exists so
 * that nobody fills in a form to be refused at the end of it, which is exactly
 * what P-4 fixed for suspension.
 *
 * **The messages are the server's, character for character.** They are
 * restated here rather than fetched because the page has to render them before
 * any request is made; `server/src/modules/tax/buyGate.ts` holds the originals
 * and `buyGate.test.ts` reads both off disk and fails if they drift.
 */
export type BuyBlockReason = 'business_details' | 'eu_vat_missing' | 'eu_vat_invalid';

export const BUY_BLOCK_MESSAGES: Record<BuyBlockReason, string> = {
  business_details:
    "Before buying, add your business website and confirm below that you're buying for your business.",
  eu_vat_missing: 'Buyers in the EU need a valid VAT number. Add yours in Account settings.',
  eu_vat_invalid:
    "That VAT number isn't showing as valid on the EU's VIES register. Check it in Account settings, or contact us at info@entitylinks.com.",
};

/** `null` when the account may buy. */
export function buyBlockReason(agency: SessionAgency | null | undefined): BuyBlockReason | null {
  if (!agency) return null;

  const hasWebsite = (agency.businessWebsite ?? '').trim() !== '';
  if (!hasWebsite || agency.businessConfirmedAt === null) return 'business_details';

  if (!EU_COUNTRIES.includes(agency.country.toUpperCase())) return null;

  if ((agency.vatNumber ?? '').trim() === '') return 'eu_vat_missing';
  if (agency.taxIdStatus === 'invalid') return 'eu_vat_invalid';

  // `unavailable` lets them through - ruling 4. So does `unchecked`, which is
  // the same situation before the call has happened.
  return null;
}
